import React, { useEffect, useState } from 'react';
import { isbot } from 'isbot';
import loadable from '@loadable/component';
import bemCn from 'bem-cn';
import background from '../../assets/img/sniff-seo-bg-header-d.jpg?sizes[]=1200,sizes[]=1920';
import backgroundMobile from '../../assets/img/sniff-seo-bg-header-m.jpg?sizes[]=500';
import { omit } from '../../helpers/omit';
import { states } from '../../consts/states';
import CookieService from '../../services/cookie.service';
import { TOP_CITIES } from '../../consts/top-cities';
import { EVENTS_ANALYTICS, EVENTS_NAME } from '../../consts/event.consts';
import { trackGtm } from '../../helpers/event-tracking';
import { SIGN_UP_SRC } from '../../consts/profile.const';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { RouteFormatter } from '../../routes';
import { SF_SIZE_OPTIONS } from '../../consts/search-filter.const';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { getSearchFiltersForServer } from '../listings/utils';
import { getCountSearchFilters } from '../../components/search/utils';
import DesignedForPrivate from '../../components/designed-for-private';
import RenderDetailedHome from './render-detailed-home';
import HomeTopSticky from './top-sticky';
import SniffButton from '../../components/sniff-button';
import BigPicture from '../../components/big-picture';
import './style.scss';

const useMutateViewRevisedHome = require('../../services/hooks/useMutateViewRevisedHome');

const b = bemCn('home');

const BlurSignupModal = loadable(() => import('../../components/modal-blur-signup'));

const HomePage = ({ useStateSSR, useEffectSSR, useRefSSR, homePageSSR, useMutateTrackEvent, serverSSR }) => {
    const isMobile = useMobileScreen();
    const { trackEvent } = useMutateTrackEvent ? useMutateTrackEvent() : {};
    const { viewRevisedHome } = serverSSR ? {} : useMutateViewRevisedHome.default();
    const [isBlurTest, setIsBlurTest] = useState(null);
    const [mainTitleClass, setMainTitleClass] = useState('snif-l2 text-white text-center');

    const handleSubClick = (e, opt) => {
        e?.preventDefault();
        const filtersNew = getSearchFiltersForServer(opt);
        const count = 1;
        const filters = omit(filtersNew, ['availability', 'openNow']);
        CookieService.set(COOKIE_PARAM_NAME.SEARCH_FILTERS, JSON.stringify({ filters, count }), { expires: 365 });
        if (isMobile) {
            window.open(RouteFormatter.listings({}));
        } else {
            window.open(RouteFormatter.listings({}), '_blank');
        }
    };

    const handleExplore = (type) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (type === 'Size') {
            const filtersNew = getSearchFiltersForServer({ showSize: true, minimumSize: SF_SIZE_OPTIONS[1] });
            const count = getCountSearchFilters(filtersNew);
            const filters = omit(filtersNew, ['availability', 'openNow']);
            CookieService.set(COOKIE_PARAM_NAME.SEARCH_FILTERS, JSON.stringify({ filters, count }), { expires: 365 });
        }
        window.open(RouteFormatter.listings({}));
    };

    const handleSubStatsClick = (type) => {
        const filtersNew = getSearchFiltersForServer({});
        filtersNew.amenities = [type];
        const count = getCountSearchFilters(filtersNew);
        const filters = omit(filtersNew, ['availability', 'openNow']);
        CookieService.set(COOKIE_PARAM_NAME.SEARCH_FILTERS, JSON.stringify({ filters, count }), { expires: 365 });
        window.open(RouteFormatter.listings({}));
    };

    const handleFreeTrial = () => {
        CookieService.set(COOKIE_PARAM_NAME.SIGN_UP_FROM, SIGN_UP_SRC.SNIFFPASS_HOME_WEB, { expires: 1 });
        CookieService.set(COOKIE_PARAM_NAME.REDIRECT, RouteFormatter.subscriptionsNew(null, { query: { home_trial: true } }));
        setIsBlurTest(true);
    };

    useEffect(() => {
        if (useMutateTrackEvent) {
            trackEvent({ name: EVENTS_NAME.home_view });
        }
    }, [useMutateTrackEvent]);

    useEffect(() => {
        if (!isMobile) {
            setMainTitleClass('snif-xl snif-semibold');
        }
    }, [isMobile]);

    useEffect(() => {
        const isBot = isbot(window.navigator.userAgent);
        if (isBot) return;

        const sessionUuid = CookieService.get(COOKIE_PARAM_NAME.SESSION_UUID);
        const reviseCookie = CookieService.get(COOKIE_PARAM_NAME.FRONT_REVISE_HOME);
        const isReviseHomeControl = reviseCookie === 'OLD';
        trackGtm({ action: EVENTS_ANALYTICS.screen_home, props: { event_category: 'View_front' } });

        if (isReviseHomeControl) {
            viewRevisedHome({ variables: { testGroup: false, sessionUuid } });
        }
    }, []);

    return (
        <>
            <div className={b()}>
                <HomeTopSticky {...{ useStateSSR }} />
                <div id="hero" className="main-action-title">
                    <BigPicture
                        background={background}
                        backgroundSm={backgroundMobile}
                        noBackground={true}
                        right={true}
                        height={700}
                        heightSm={360}
                        alt="Sniffspot main banner"
                    >
                        <h1 className={mainTitleClass}>Rent safe and private dog parks hosted by locals</h1>
                        <div className="main-action-cta">
                            <a href={RouteFormatter.listings({})}>
                                <SniffButton className="main-action-button" size="lg">
                                    Explore spots near me
                                </SniffButton>
                            </a>
                            <SniffButton className="main-action-button second" color="secondary" size="lg" onClick={handleFreeTrial}>
                                Get 10 free credit with trial
                            </SniffButton>
                        </div>
                    </BigPicture>
                    <DesignedForPrivate banner home />
                </div>
                <RenderDetailedHome
                    SSR
                    {...{
                        isMobile,
                        cities: TOP_CITIES,
                        states,
                        handleSubClick,
                        handleExplore,
                        handleSubStatsClick,
                        useStateSSR,
                        useEffectSSR,
                        useRefSSR,
                        homePageSSR,
                        serverSSR,
                    }}
                />
            </div>
            <BlurSignupModal
                {...{
                    isBlurTest,
                    setIsBlurTest,
                    isMobile,
                    useStateSSR,
                }}
            />
        </>
    );
};

export default HomePage;
